import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { CameraCapture } from '@/components/camera/CameraCapture';
import { classNames } from '@/lib/utils';
import type { BacklightStatus } from '@/types';

interface BacklightFormProps {
    onSubmit: (data: { status: BacklightStatus; notes?: string; imageBlob: Blob; imageUrl: string }) => void;
    onBack?: () => void;
}

const STATUS_OPTIONS: { value: BacklightStatus; label: string; icon: string; color: string }[] = [
    { value: 'implemented', label: 'Implementado', icon: '✅', color: 'status-success' },
    { value: 'not_implemented', label: 'Não Implementado', icon: '❌', color: 'status-danger' },
    { value: 'defective', label: 'Com Defeito', icon: '⚠️', color: 'status-warning' },
];

export function BacklightForm({ onSubmit, onBack }: BacklightFormProps) {
    const [status, setStatus] = useState<BacklightStatus | null>(null);
    const [notes, setNotes] = useState('');
    const [capturedImage, setCapturedImage] = useState<{ blob: Blob; url: string } | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [error, setError] = useState('');

    const handleCapture = (blob: Blob, url: string) => {
        setCapturedImage({ blob, url });
        setShowCamera(false);
    };

    const handleSubmit = () => {
        setError('');

        if (!status) {
            setError('Selecione o status do backlight');
            return;
        }

        if (!capturedImage) {
            setError('A foto do backlight é obrigatória');
            return;
        }

        onSubmit({
            status,
            notes: notes || undefined,
            imageBlob: capturedImage.blob,
            imageUrl: capturedImage.url,
        });
    };

    // Tela de câmera em fullscreen
    if (showCamera) {
        return (
            <div className="fixed inset-0 z-50 bg-black">
                <CameraCapture
                    onCapture={handleCapture}
                    onCancel={() => setShowCamera(false)}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <Card>
                <CardContent>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-brand-surface flex items-center justify-center">
                            <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-content-title">
                                Auditoria de Backlight
                            </h2>
                            <p className="text-sm text-content-muted">
                                Verifique o status da iluminação
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Status selection */}
            <Card>
                <CardContent className="space-y-4">
                    <label className="block text-sm font-medium text-content-title">
                        Status do Backlight
                    </label>

                    <div className="grid gap-3">
                        {STATUS_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => setStatus(option.value)}
                                className={classNames(
                                    'w-full p-4 rounded-lg border-2 flex items-center gap-4 transition-all',
                                    status === option.value
                                        ? `border-${option.color} bg-${option.color}/10`
                                        : 'border-ui-border hover:border-gray-300'
                                )}
                            >
                                <span className="text-2xl">{option.icon}</span>
                                <span className={classNames(
                                    'text-lg font-medium',
                                    status === option.value ? `text-${option.color}` : 'text-content-body'
                                )}>
                                    {option.label}
                                </span>
                                {status === option.value && (
                                    <svg className={`w-6 h-6 ml-auto text-${option.color}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Foto */}
            <Card>
                <CardContent className="space-y-4">
                    <label className="block text-sm font-medium text-content-title">
                        Foto do Backlight <span className="text-status-danger">*</span>
                    </label>

                    {capturedImage ? (
                        <div className="relative rounded-lg overflow-hidden">
                            <img
                                src={capturedImage.url}
                                alt="Foto do backlight"
                                className="w-full h-48 object-cover"
                            />
                            <button
                                onClick={() => setCapturedImage(null)}
                                className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowCamera(true)}
                            className="w-full h-32 border-2 border-dashed border-ui-border rounded-lg flex flex-col items-center justify-center gap-2 text-content-muted hover:border-brand-primary hover:text-brand-primary transition-colors"
                        >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-sm">Tirar Foto (obrigatório)</span>
                        </button>
                    )}
                </CardContent>
            </Card>

            {/* Observações */}
            <Card>
                <CardContent>
                    <label className="block text-sm font-medium text-content-title mb-2">
                        Observações (opcional)
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ex: Lâmpadas queimadas no lado esquerdo..."
                        rows={3}
                        className="w-full px-4 py-3 border border-ui-border rounded-lg text-content-body placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
                    />
                </CardContent>
            </Card>

            {error && (
                <div className="p-3 bg-status-danger/10 border border-status-danger/20 rounded-lg">
                    <p className="text-sm text-status-danger text-center">{error}</p>
                </div>
            )}

            {/* Botões */}
            <div className="flex gap-3">
                {onBack && (
                    <Button variant="secondary" size="lg" fullWidth onClick={onBack}>
                        Voltar
                    </Button>
                )}
                <Button variant="primary" size="lg" fullWidth onClick={handleSubmit}>
                    Próximo
                </Button>
            </div>
        </div>
    );
}

export default BacklightForm;
